import { GridTile } from "./GridTile.js";
// import { ClueBoard } from './ClueBoard.js';
export class GridModelWord {
  /**
   * @param {GridTile[]} tiles - Array of GridTile objects, one for each character of the word
   * @param {string} orientation - "ACROSS" or "DOWN"
   * @param {number} row - Starting row (0-based)
   * @param {number} column - Starting column (0-based)
   */
  constructor(tiles, orientation, row, column) {
    if (
      !Array.isArray(tiles) ||
      tiles.length === 0 ||
      !tiles.every((tile) => tile instanceof GridTile)
    ) {
      throw new Error(
        "GridModelWord constructor: 'tiles' must be a non-empty array of GridTile."
      );
    }
    if (
      typeof orientation !== "string" ||
      (orientation !== "ACROSS" && orientation !== "DOWN")
    ) {
      throw new Error(
        "GridModelWord constructor: 'orientation' must be one of the following: 'ACROSS' or 'DOWN'."
      );
    }
    if (typeof row !== "number" || isNaN(row)) {
      throw new Error(
        "GridModelWord constructor: 'row' must be a valid number."
      );
    }
    if (typeof column !== "number" || isNaN(column)) {
      throw new Error(
        "GridModelWord constructor: 'column' must be a valid number."
      );
    }

    // Clue board properties
    this.itsClueBoard = null;
    this.attachedToClue = null;
    this.solutionClue = null;

    this.orientation = orientation;
    this.row = row;
    this.col = column;

    this.tiles = tiles;
    this.length = tiles.length;
    this.guessChars = Array(tiles.length).fill(" ");
    this.charsFilled = 0;
    this.full = false;
  }

  /**
   * @returns {GridTile[]} - Array of GridTile objects
   */
  getTiles() {
    return this.tiles;
  }
  /**
   * @returns {string} - Orientation of the word ("ACROSS" or "DOWN")
   */
  getOrientation() {
    return this.orientation;
  }
  /**
   * @returns {ClueBoard} - The board this word is attached to
   */
  getClueBoard() {
    return this.itsClueBoard;
  }
  /**
   * Set the clue board this word is associated with.
   * @param {ClueBoard} clueBoard - The ClueBoard instance.
   */
  setClueBoard(clueBoard) {
    this.itsClueBoard = clueBoard;
  }
  /**
   * @returns {number} - Starting row (1-based)
   */
  getRow() {
    return this.row;
  }
  /**
   * @returns {number} - Starting column (1-based)
   */
  getColumn() {
    return this.col;
  }
  /**
   * @returns {string} - The word formed by the tiles
   */
  getWordValue() {
    return this.tiles.map((tile) => tile.getSolutionValue()).join("");
  }

  getFirstTile() {
    return this.tiles[0];
  }

  getLastTile() {
    return this.tiles[this.tiles.length - 1];
  }
  /**
   * @returns {number} - The number of characters filled in the guess layer
   */
  getCharsFilled() {
    return this.charsFilled;
  }

  /**
   * @returns {string} - The word as currently filled by the user (guess layer)
   */
  getGuessValue() {
    return this.guessChars.join("");
  }

  /**
   * Returns the first empty GridTile in the word, or null if all are filled.
   * @returns {GridTile|null}
   */
  getFirstEmptyTile() {
    return (
      this.tiles.find((tile) => !tile.getValue() || tile.getValue() === "") ||
      null
    );
  }

  /**
   * Sets a guess character at the specified tile index.
   * @param {number} index - The index of the tile (0-based).
   * @param {string} char - The guessed character to set.
   */
  setGuessChar(index, char) {
    if (typeof index !== "number" || index < 0 || index >= this.tiles.length) {
      throw new Error("setGuessChar: index out of bounds.");
    }
    if (typeof char !== "string" || char.length !== 1) {
      throw new Error("setGuessChar: char must be a single character string.");
    }
    if (this.guessChars[index] === char) {
      return;
    }

    // remove any previously attached to clue word
    if (this.full && this.attachedToClue) {
      if (typeof this.attachedToClue.unuse === "function") {
        this.attachedToClue.unuse();
      }
      this.attachedToClue = null;
    }
    // console.log(`Typing: ${char} at index: ${index}`)
    this.guessChars[index] = char;

    // // Update charsFilled and full status
    this.charsFilled = this.guessChars.filter((c) => c && c !== " ").length;
    this.full = this.charsFilled === this.tiles.length;
    // If the word is full, find the corresponding clue word by guess value and set its 'used' flag to true
    if (
      this.full &&
      this.itsClueBoard &&
      typeof this.itsClueBoard.getClueWordByValue === "function"
    ) {
      const guess = this.getGuessValue();
      const clueWord = this.itsClueBoard.getClueWordByValue(guess);
      if (clueWord && typeof clueWord.setUsed === "function") {
        if (window.CLUEBOARD_DEBUG_LOG) console.log(`Clue used: ${guess}`);
        clueWord.setUsed();
        this.attachedToClue = clueWord;
        this.itsClueBoard.wordFilledSignal(guess);
      }
      this.itsClueBoard.render();
    }
  }

  /**
   * Removes the guess character at the specified tile index.
   * @param {number} index - The index of the tile (0-based).
   */
  removeGuessChar(index) {
    if (typeof index !== "number" || index < 0 || index >= this.tiles.length) {
      throw new Error("removeGuessChar: index out of bounds.");
    }
    if (this.guessChars[index] !== " ") {
      this.guessChars[index] = " ";
      this.charsFilled = this.guessChars.filter((c) => c && c !== " ").length;
      this.full = this.charsFilled === this.tiles.length;
      // If the word is no longer full, set the corresponding clue word's used flag to false
      if (this.attachedToClue) {
        const clueWord = this.attachedToClue;
        if (clueWord && typeof clueWord.unuse === "function") {
          clueWord.unuse();
          this.attachedToClue = null;
          this.itsClueBoard.render();
        }
      }
    }
  }
}
